// The company's setup after Getting started (settings prototype): the price book, departments and
// job types, tax zones and message templates as Settings tabs; a person's profile from People; a
// truck or warehouse from Inventory. Every write is one existing action, the server decides who may,
// and a refusal shows its words; a role that can't change a part sees it read-only, and says why.

import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { client } from '../api.ts';
import { money } from '../review/review-model.ts';
import { Loading } from '../shared/Loading.tsx';
import { Sheet, SheetButtons, useWrite } from '../shared/EditSheet.tsx';
import { refusal } from '../history/customer-edit-model.ts';
import { PARAGRAPH_FIELDS, type PictureFirst } from '@flowology/domain';
import {
  CUSTOMER_TEXT_FIELDS,
  paragraphPreview,
  paragraphProblem,
  textForm,
  textWrites,
  type TextForm,
} from './customer-text-model.ts';
import type { SettingRow } from './settings-model.ts';
import {
  ITEM_TYPES,
  TAX_CLASSES,
  UNIT_RULES,
  componentInput,
  emptyItemForm,
  emptyRateForm,
  emptyUnitForm,
  itemForm,
  itemEditable,
  itemInput,
  jobTypeForm,
  jobTypeDefaultsInput,
  jobTypeInput,
  newItemInput,
  hasDrawing,
  importedWords,
  percentOf,
  photosFirst,
  profileForm,
  profileInput,
  inactiveWords,
  ratesNow,
  rowPicture,
  showFirstInput,
  stockInput,
  templateInput,
  unitDefaultsInput,
  unitInput,
  NOTHING,
  unknownFields,
  zoneInput,
  type Item,
  type JobType,
  type RateForm,
  type Staff,
  type Template,
  type Unit,
  type UnitForm,
  type Zone,
} from './setup-model.ts';
import './setup.css';
import { PricebookSpreadsheet } from './PricebookSpreadsheet.tsx';
import { PricebookArtPanel } from './PricebookArt.tsx';
import { MIRROR_LINE } from '../shared/mirror.ts';
import { useDesign } from '../blueprint/design.ts';
import { useShownTheme } from '../blueprint/theme.ts';
import { CATALOG_LINK, categoryOptions, type Category } from '../inventory/catalog-model.ts';
import { StaffPhoto } from './StaffPhoto.tsx';
import { PricebookHistory } from './PricebookHistory.tsx';
import { REASON_MAX, withReason } from './item-history-model.ts';
import { LicenceKindsCard, StaffLicences, useLicenceKinds } from './Licences.tsx';
import { SectionRules } from './RuleSection.tsx';
import { settingsRights } from './settings-model.ts';
import type { LicenceKind } from './licences-model.ts';
import { ItemMedia, ItemPicture, ItemUses, Section } from '@flowology/ui-web';

/** Loads a list once, and again on reload(). */
/** Blueprint's lists show five rows, then "Show all" (DESIGN.md §3); Riverstone shows every row. */
function useFive<T>(rows: T[] | null): { rows: T[] | null; more: ReactNode } {
  const bp = useDesign() === 'blueprint';
  const [all, setAll] = useState(false);
  if (!bp || all || !rows || rows.length <= 5) return { rows, more: null };
  return {
    rows: rows.slice(0, 5),
    more: (
      <button className="bp-more" onClick={() => setAll(true)}>
        Show all {rows.length}
      </button>
    ),
  };
}

function useList<T>(action: string, input: unknown, pick: (r: never) => T[]) {
  const [rows, setRows] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const r = await client.call<never>(action, input);
      setRows(pick(r));
      setError(null);
    } catch (e) {
      setError(refusal(e));
    }
    // input is a literal per call site
  }, [action, JSON.stringify(input)]);
  useEffect(() => {
    void load();
  }, [load]);
  return { rows, error, reload: load };
}

const ReadOnly = ({ who }: { who: string }) => <p className="muted small read-only">{who} changes these.</p>;

// ───────── the price book ─────────

export function PricebookTab({
  canEdit,
  mirroring = false,
  toCatalog = false,
  children,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: {
  canEdit: boolean;
  mirroring?: boolean;
  /** Blueprint: who keeps the stock goes from here to the same parts in Stock's catalog. */
  toCatalog?: boolean;
  /** Blueprint: the page's other sections, before the spreadsheet's (which is last, under the fold). */
  children?: ReactNode;
  /** The company's zone: an item's history reads in it (Blueprint). */
  timeZone?: string;
}) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Item[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState<'item' | 'category' | null>(null);
  // every category (list caps M4): an item's picker offers those still in use, and its facts name any
  const [cats, setCats] = useState<Category[]>([]);
  useEffect(() => {
    let current = true;
    client
      .call<{ categories: Category[] }>('pricebook.listCategories', {})
      .then((r) => current && setCats(r.categories))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, []);
  const [version, setVersion] = useState(0);
  // what a spreadsheet import did, said once
  const [imported, setImported] = useState<string | null>(null);
  // Blueprint: the list shows five items, then all (a search shows every match), and the spreadsheet
  // is its own section, so the price book's has two actions
  const bp = useDesign() === 'blueprint';
  const [all, setAll] = useState(false);
  // a row's drawing in the person's light or dark (BL-135), following a change while the list is open;
  // and which the company shows first when an item has both (pricebook.art.showFirst, listItems says)
  const theme = useShownTheme();
  const [first, setFirst] = useState<PictureFirst>('drawing');
  useEffect(() => {
    let current = true;
    const t = setTimeout(() => {
      // every item, a page at a time: the server gives 200 at most, and a company's book runs past it
      void (async () => {
        const got: Item[] = [];
        let after: { name: string; id: string } | null = null;
        do {
          const r: {
            items: Item[];
            next: { name: string; id: string } | null;
            showFirst?: PictureFirst;
          } = await client.call('pricebook.listItems', {
            ...(query.trim() ? { query: query.trim() } : {}),
            includeInactive: true,
            includeNotForSale: true,
            limit: 200,
            ...(after ? { after } : {}),
          });
          if (!current) return;
          got.push(...r.items);
          if (r.showFirst) setFirst(r.showFirst);
          after = r.next;
        } while (after);
        setItems(got);
        setError(null);
      })().catch((e: unknown) => current && setError(refusal(e)));
    }, 200);
    return () => {
      current = false;
      clearTimeout(t);
    };
  }, [query, version]);
  const done = () => {
    setOpen(null);
    setAdding(null);
    setVersion((v) => v + 1);
  };
  const shown = items && bp && !all && !query.trim() ? items.slice(0, 5) : items;
  const spreadsheet = (
    <>
      {/* to and from a spreadsheet: the download for everyone here, the import for those who edit */}
      <PricebookSpreadsheet
        canEdit={canEdit && !mirroring}
        onImported={(said) => {
          setImported(said);
          setVersion((v) => v + 1);
        }}
      />
      {imported && (
        <p className="notice ok" role="status">
          {imported}
        </p>
      )}
    </>
  );
  return (
    <>
      <section className="sheet setup-card" aria-labelledby="pb-title">
        <div className="side-head">
          <h2 id="pb-title">Price book</h2>
          {canEdit && (
            <div className="row">
              <button className="btn primary" onClick={() => setAdding('item')}>
                Add an item
              </button>
              {/* the categories are ServiceTitan's while the company mirrors it (BL-096) */}
              {!mirroring && (
                <button className="btn quiet" onClick={() => setAdding('category')}>
                  New category
                </button>
              )}
            </div>
          )}
        </div>
        {!bp && spreadsheet}
        {!canEdit && <ReadOnly who="Only the owner or a manager" />}
        <label className="search">
          <span>Find an item</span>
          <input
            className="field"
            type="search"
            placeholder="Name or code"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        {error && <p className="error">{error}</p>}
        {!items && !error && <Loading error={null} what="the price book" />}
        {items && !items.length && <p className="muted">{query ? 'Nothing matches.' : 'No items yet.'}</p>}
        {items && items.length > 0 && (
          <div className="tablewrap">
            <table className="t">
              <thead>
                <tr>
                  {bp && (
                    <th className="pb-pic">
                      <span className="sr">Picture</span>
                    </th>
                  )}
                  <th>Item</th>
                  <th className="pb-type">Type</th>
                  <th className="num">Price</th>
                  <th className="num pb-mem">Members</th>
                  <th>
                    <span className="sr">Status and actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(shown ?? items).map((i) => (
                  <tr key={i.id} className={i.active ? '' : 'off'}>
                    {bp && (
                      <td className="pb-pic">
                        <ItemPicture data={{ name: i.name, picture: rowPicture(i, theme, first) }} />
                      </td>
                    )}
                    <td>
                      <b>{i.name}</b>
                      <span className="muted small"> {i.code}</span>
                    </td>
                    <td className="pb-type">{ITEM_TYPES.find((t) => t[0] === i.type)?.[1] ?? i.type}</td>
                    <td className="num">
                      {money(i.priceCents)}
                      <span className="muted small"> / {i.unit}</span>
                    </td>
                    <td className="num pb-mem">
                      {i.memberPriceCents === null ? '—' : money(i.memberPriceCents)}
                    </td>
                    <td className="pb-act">
                      {!i.active && <span className="chip say">Not offered</span>}
                      <button
                        className="btn quiet sm"
                        aria-label={`${itemEditable(i, canEdit, mirroring) ? 'Edit' : 'Open'} ${i.name}`}
                        onClick={() => setOpen(i.id)}
                      >
                        {itemEditable(i, canEdit, mirroring) ? 'Edit' : 'Open'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {items && shown && shown.length < items.length && (
          <button className="bp-more" onClick={() => setAll(true)}>
            Show all {items.length}
          </button>
        )}
        {open && (
          <ItemSheet
            id={open}
            canEdit={canEdit}
            mirroring={mirroring}
            cats={cats}
            timeZone={timeZone}
            first={first}
            onSaved={done}
            onChanged={() => setVersion((v) => v + 1)}
            onPictures={() => setVersion((v) => v + 1)}
            onClose={() => setOpen(null)}
          />
        )}
        {adding === 'item' && <NewItemSheet cats={cats} onSaved={done} onClose={() => setAdding(null)} />}
        {adding === 'category' && (
          <NewCategorySheet
            onSaved={(c) => {
              setCats([...cats, { ...c, parentId: null, active: true }]);
              setAdding(null);
            }}
            onClose={() => setAdding(null)}
          />
        )}
      </section>
      {children}
      {bp && (
        <section className="sheet setup-card" aria-labelledby="pb-sheet-title">
          <h2 id="pb-sheet-title">As a spreadsheet</h2>
          {spreadsheet}
        </section>
      )}
      {bp && toCatalog && (
        // the catalog is these same items, not a copy: their units, vendor and bin are Stock's
        <section className="sheet setup-card" aria-labelledby="pb-stock-title">
          <h2 id="pb-stock-title">In stock</h2>
          <p className="small">
            The parts here are the ones Stock keeps. What the vendor sells each in, its vendor, bin and levels
            are in <a href={CATALOG_LINK}>Catalog and prices</a>.
          </p>
        </section>
      )}
    </>
  );
}

function CategoryPick({
  cats,
  value,
  current,
  onChange,
}: {
  cats: Category[];
  value: string;
  current?: string | null;
  onChange: (v: string) => void;
}) {
  if (!cats.length) return null;
  return (
    <label className="f">
      Category
      <select className="field" value={value} onChange={(e) => onChange(e.target.value)}>
        {current ? <option value={current}>As it is</option> : <option value="">None</option>}
        {current && <option value="">None</option>}
        {categoryOptions(cats).map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ItemSheet({
  id,
  canEdit,
  mirroring,
  cats,
  timeZone,
  first = 'drawing',
  onSaved,
  onChanged,
  onPictures,
  onClose,
}: {
  id: string;
  canEdit: boolean;
  /** ServiceTitan's items are read here while the company mirrors it; Flowology's own are edited. */
  mirroring: boolean;
  cats: Category[];
  timeZone: string;
  /** Which picture the company shows first when an item has both (pricebook.art.showFirst). */
  first?: PictureFirst;
  onSaved: () => void;
  /** Blueprint: values were put back from its history, the sheet still open; the list reads again. */
  onChanged: () => void;
  /** Its pictures changed (the sheet stays open): the list's picture follows. */
  onPictures?: () => void;
  onClose: () => void;
}) {
  const [item, setItem] = useState<Item | null>(null);
  const [f, setF] = useState<ReturnType<typeof itemForm> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Blueprint: why the change, kept in the item's history beside it; and a read again after a put-back
  const bp = useDesign() === 'blueprint';
  const [reason, setReason] = useState('');
  const [reads, setReads] = useState(0);
  const w = useWrite(JSON.stringify(f) + (bp ? reason : ''));
  // Blueprint: the item's photos above its fields, under its drawing, which shows first once one has
  // passed (BL-109), or above it when the company shows imported photos first and the item has one; who
  // may change it picks the photo that leads them. Imported ones are kept for reference and say so; with
  // a drawing and no photos, nothing says it has no picture.
  const theme = useShownTheme();
  const pw = useWrite(JSON.stringify(item?.media?.map((m) => [m.fileId, m.isPrimary])));
  const showFirst = async (fileId: string) => {
    if (!item) return;
    const r = await pw.run<{ rowVersion: number; media: NonNullable<Item['media']> }>(
      'pricebook.setMedia',
      showFirstInput(item, fileId),
    );
    if (!r) return;
    // the item's new version, so a save after this isn't refused as someone else's change
    setItem({ ...item, rowVersion: r.rowVersion, media: r.media });
    onPictures?.();
  };
  const media = item?.media ?? [];
  const lead = !!item && photosFirst(item, theme, first);
  const panel = item && <PricebookArtPanel itemId={item.id} name={item.name} />;
  const pictures = bp && item && (media.length > 0 || !hasDrawing(item)) && (
    <>
      <ItemMedia
        data={{ name: item.name, media }}
        from={importedWords(item) ?? undefined}
        onFirst={itemEditable(item, canEdit, mirroring) ? (m) => void showFirst(m) : undefined}
        busy={pw.busy}
      />
      {pw.error && (
        <p className="error" role="alert">
          {pw.error}
        </p>
      )}
      {lead && panel}
    </>
  );
  useEffect(() => {
    client
      .call<Item>('pricebook.getItem', { id })
      .then((i) => {
        setItem(i);
        setF(itemForm(i));
      })
      .catch((e: unknown) => setLoadError(refusal(e)));
  }, [id, reads]);
  const set =
    (k: keyof NonNullable<typeof f>) =>
    (e: { target: { value: string; checked?: boolean; type?: string } }) => {
      if (!f) return;
      setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
      w.setError(null);
    };
  const save = async () => {
    if (!item || !f) return;
    const built = itemInput(item, f);
    const r = await w.run<Item>('pricebook.updateItem', bp ? withReason(built, reason) : built);
    if (r) onSaved();
  };
  const history = item && bp && (
    <PricebookHistory
      item={item}
      timeZone={timeZone}
      canPutBack={itemEditable(item, canEdit, mirroring)}
      onPutBack={() => {
        setReads((n) => n + 1);
        onChanged();
      }}
    />
  );
  return (
    <Sheet title={item ? `${item.name} · ${item.code}` : 'Item'} onClose={onClose}>
      {item && !lead && panel}
      {!item || !f ? (
        <Loading error={loadError} what="the item" />
      ) : !itemEditable(item, canEdit, mirroring) ? (
        <ReadOnlySheet onClose={onClose}>
          {canEdit && <p className="muted small">{MIRROR_LINE}</p>}
          {pictures}
          <ItemFacts item={item} cats={cats} />
          <ItemUsesPart item={item} />
          {history}
        </ReadOnlySheet>
      ) : (
        <>
          {pictures}
          <fieldset className="plain">
            <div className="fgrid">
              <label className="f">
                Name
                <input className="field" value={f.name} onChange={set('name')} autoFocus />
              </label>
              <label className="f">
                Sold by
                <input
                  className="field"
                  value={f.unit}
                  onChange={set('unit')}
                  placeholder="each, hour, foot"
                />
              </label>
              <label className="f">
                Price
                <input className="field num" inputMode="decimal" value={f.price} onChange={set('price')} />
              </label>
              <label className="f">
                Member price
                <input
                  className="field num"
                  inputMode="decimal"
                  value={f.memberPrice}
                  onChange={set('memberPrice')}
                  placeholder="the member discount, if empty"
                />
              </label>
              {item.costCents !== undefined && (
                <label className="f">
                  Our cost (owner only)
                  <input className="field num" inputMode="decimal" value={f.cost} onChange={set('cost')} />
                </label>
              )}
              <label className="f">
                Tax
                <select className="field" value={f.taxClass} onChange={set('taxClass')} disabled={!f.taxable}>
                  {TAX_CLASSES.map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <CategoryPick
                cats={cats}
                value={f.categoryId}
                current={item.categoryId}
                onChange={(categoryId) => setF({ ...f, categoryId })}
              />
            </div>
            <label className="f">
              What it includes
              <textarea className="field area" value={f.description} onChange={set('description')} />
            </label>
            <label className="checkrow">
              <input type="checkbox" checked={f.taxable} onChange={set('taxable')} /> Taxable
            </label>
            <label className="checkrow">
              <input type="checkbox" checked={f.active} onChange={set('active')} /> Offered (techs can add it
              to estimates and invoices)
            </label>
            {bp && (
              <label className="f">
                Why the change
                <input
                  className="field"
                  value={reason}
                  maxLength={REASON_MAX}
                  placeholder="Supplier price went up"
                  onChange={(e) => {
                    setReason(e.target.value);
                    w.setError(null);
                  }}
                />
                <span className="muted small">Kept in its history. Customers don’t see it.</span>
              </label>
            )}
          </fieldset>
          <ItemUsesPart item={item} />
          <p className="muted small">
            A new price applies to estimates and invoices from now on; ones already made keep theirs.
          </p>
          <SheetButtons
            w={w}
            label="Save"
            busyLabel="Saving…"
            onSave={() => void save()}
            onCancel={onClose}
          />
          {history}
        </>
      )}
    </Sheet>
  );
}

/** The item as words, for a role that reads the price book but doesn't change it: no fields to type in. */
function ItemFacts({ item, cats }: { item: Item; cats: { id: string; name: string }[] }) {
  const taxClass = TAX_CLASSES.find(([k]) => k === item.taxClass)?.[1] ?? item.taxClass;
  const category = cats.find((c) => c.id === item.categoryId)?.name;
  return (
    <>
      <dl className="kv item-facts">
        <dt>Price</dt>
        <dd>
          {money(item.priceCents)} / {item.unit}
        </dd>
        <dt>Member price</dt>
        <dd>
          {item.memberPriceCents === null
            ? 'The member discount'
            : `${money(item.memberPriceCents)} / ${item.unit}`}
        </dd>
        {item.costCents !== undefined && (
          <>
            <dt>Our cost</dt>
            <dd>{item.costCents === null ? '—' : money(item.costCents)}</dd>
          </>
        )}
        <dt>Tax</dt>
        <dd>{item.taxable ? taxClass : 'Not taxed'}</dd>
        {category && (
          <>
            <dt>Category</dt>
            <dd>{category}</dd>
          </>
        )}
        <dt>Offered</dt>
        <dd>{item.active ? 'Yes, techs can add it' : 'No'}</dd>
        {item.description && (
          <>
            <dt>What it includes</dt>
            <dd className="item-desc">{item.description}</dd>
          </>
        )}
      </dl>
    </>
  );
}

/**
 * What a service uses and its warranty (pricebook.getItem's materials and details; Blueprint only), read
 * only either way: ServiceTitan's service links, loaded by the import, and kept there while it mirrors.
 * Our cost on each part only when the answer carries it. Nothing when the item has neither.
 */
function ItemUsesPart({ item }: { item: Item }) {
  const bp = useDesign() === 'blueprint';
  const uses = item.materials ?? [];
  if (!bp || (!uses.length && !item.details)) return null;
  const service = item.type === 'service';
  return (
    <div className="flw item-uses">
      <Section title={service ? 'What it uses' : 'Warranty and terms'}>
        <ItemUses data={{ uses, details: item.details ?? null }} empty={service ? undefined : null} />
      </Section>
    </div>
  );
}

/** A setup sheet for a role that reads it but doesn't change it: the facts as words, who changes them, and Close. */
function ReadOnlySheet({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <>
      {children}
      <ReadOnly who="Only the owner or a manager" />
      <div className="row">
        <button className="btn quiet" onClick={onClose}>
          Close
        </button>
      </div>
    </>
  );
}

function NewItemSheet({
  cats,
  onSaved,
  onClose,
}: {
  cats: Category[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(emptyItemForm);
  const w = useWrite(JSON.stringify(f));
  const set = (k: keyof typeof f) => (e: { target: { value: string; checked?: boolean; type?: string } }) => {
    setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    w.setError(null);
  };
  const save = async () => {
    const r = await w.run<Item>('pricebook.createItem', newItemInput(f));
    if (r) onSaved();
  };
  return (
    <Sheet title="Add an item" onClose={onClose}>
      <div className="fgrid">
        <label className="f">
          Name
          <input className="field" value={f.name} onChange={set('name')} autoFocus />
        </label>
        <label className="f">
          Type
          <select className="field" value={f.type} onChange={set('type')}>
            {ITEM_TYPES.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          Code
          <input
            className="field"
            value={f.code}
            onChange={set('code')}
            placeholder="made from the name if empty"
          />
        </label>
        <label className="f">
          Sold by
          <input className="field" value={f.unit} onChange={set('unit')} />
        </label>
        <label className="f">
          Price
          <input
            className="field num"
            inputMode="decimal"
            value={f.price}
            onChange={set('price')}
            placeholder="129.00"
          />
        </label>
        <label className="f">
          Member price
          <input
            className="field num"
            inputMode="decimal"
            value={f.memberPrice}
            onChange={set('memberPrice')}
            placeholder="optional"
          />
        </label>
        <CategoryPick
          cats={cats}
          value={f.categoryId}
          onChange={(categoryId) => setF({ ...f, categoryId })}
        />
      </div>
      <label className="checkrow">
        <input type="checkbox" checked={f.taxable} onChange={set('taxable')} /> Taxable
      </label>
      <SheetButtons
        w={w}
        label="Add item"
        busyLabel="Adding…"
        disabled={!f.name.trim() || !f.price.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

function NewCategorySheet({
  onSaved,
  onClose,
}: {
  onSaved: (c: { id: string; name: string }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const w = useWrite(name);
  const save = async () => {
    const n = name.trim();
    const r = await w.run<{ id: string }>(
      'pricebook.createCategory',
      n ? { input: { name: n } } : { error: 'Name the category.' },
    );
    if (r) onSaved({ id: r.id, name: n });
  };
  return (
    <Sheet title="New category" onClose={onClose}>
      <label className="f">
        Name
        <input
          className="field"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="like Water heaters"
          maxLength={100}
          autoFocus
        />
      </label>
      <p className="muted small">You can then put items in it from their own sheet.</p>
      <SheetButtons
        w={w}
        label="Add category"
        busyLabel="Adding…"
        disabled={!name.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ───────── a person's profile ─────────

export function ProfileSheet({
  person,
  timeZone,
  onSaved,
  onPhoto,
  onClose,
}: {
  person: Staff;
  timeZone: string;
  onSaved: () => void;
  /** A new photo changed the profile: read it again (its row version moves too). */
  onPhoto: () => void;
  onClose: () => void;
}) {
  const [said, setSaid] = useState<string | null>(null);
  const [f, setF] = useState(() => profileForm(person));
  const w = useWrite(JSON.stringify(f));
  // QA-722: how many visits they're booked on, read once they're unticked from working here
  const leaving = !f.active && person.status === 'active';
  const [booked, setBooked] = useState<number | null>(null);
  useEffect(() => {
    if (!leaving || booked !== null) return;
    let live = true;
    void client
      .call<{ count: number }>('staff.bookedVisits', { staffId: person.id })
      .then((r) => live && setBooked(r.count))
      .catch(() => undefined); // without it, the warning still says their visits wait
    return () => {
      live = false;
    };
  }, [leaving, booked, person.id]);
  const set = (k: keyof typeof f) => (e: { target: { value: string; checked?: boolean; type?: string } }) => {
    setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    w.setError(null);
  };
  const save = async () => {
    const r = await w.run<Staff>('staff.updateProfile', profileInput(person, f));
    if (r) onSaved();
  };
  return (
    <Sheet title={`Edit ${person.displayName}`} onClose={onClose}>
      <StaffPhoto person={person} onChanged={onPhoto} />
      {/* their licences, beside the photo (BL-027): each saved or removed on its own */}
      <StaffLicences person={person} timeZone={timeZone} onSaid={setSaid} />
      {said && (
        <p className="small" role="status">
          {said}
        </p>
      )}
      <div className="fgrid">
        <label className="f">
          Name
          <input className="field" value={f.displayName} onChange={set('displayName')} autoFocus />
        </label>
        <label className="f">
          Phone
          <input
            className="field"
            type="tel"
            value={f.phone}
            onChange={set('phone')}
            placeholder="optional"
          />
        </label>
        <label className="f">
          Email
          <input
            className="field"
            type="email"
            value={f.email}
            onChange={set('email')}
            placeholder="optional"
          />
        </label>
        <label className="f">
          Colour on the board
          <span className="color-row">
            <input
              className="field color"
              type="color"
              value={f.color || '#257570'}
              onChange={set('color')}
              aria-label="Colour"
            />
            {f.color ? f.color.toUpperCase() : 'None yet'}
          </span>
        </label>
      </div>
      <label className="f">
        Skills
        <input
          className="field"
          value={f.skills}
          onChange={set('skills')}
          placeholder="gas, backflow, drains"
        />
      </label>
      <label className="f">
        About them (on the customer’s “Meet your technician” page)
        <textarea className="field area" value={f.bio} onChange={set('bio')} maxLength={500} />
      </label>
      <label className="checkrow">
        <input type="checkbox" checked={f.isTechnician} onChange={set('isTechnician')} /> Technician (shows on
        the dispatch board)
      </label>
      <label className="checkrow">
        <input type="checkbox" checked={f.active} onChange={set('active')} /> Works here now
      </label>
      {leaving && (
        <p className="notice warn" role="status">
          {inactiveWords(person, booked)}
        </p>
      )}
      <SheetButtons w={w} label="Save" busyLabel="Saving…" onSave={() => void save()} onCancel={onClose} />
    </Sheet>
  );
}

// ───────── departments and job types ─────────

export function OrgTab({
  canEdit,
  mirroring = false,
  show = 'all',
}: {
  canEdit: boolean;
  mirroring?: boolean;
  /** Blueprint's Setup splits the page (the setup audit): Departments on Company details, the job
   *  types and licences on Job types. */
  show?: 'all' | 'departments' | 'types';
}) {
  const units = useList<Unit>(
    'businessUnits.list',
    { includeInactive: true },
    (r: { businessUnits: Unit[] }) => r.businessUnits,
  );
  const types = useList<JobType>(
    'jobTypes.list',
    { includeInactive: true },
    (r: { jobTypes: JobType[] }) => r.jobTypes,
  );
  const [unit, setUnit] = useState<Unit | 'new' | null>(null);
  const [type, setType] = useState<JobType | 'new' | null>(null);
  // whose customer text is open: the company's ('company') or a department's (BL-052)
  const [text, setText] = useState<Unit | 'company' | null>(null);
  const unitName = (id: string | null) => units.rows?.find((u) => u.id === id)?.name ?? 'Any department';
  const licences = useLicenceKinds(true);
  const kindName = (id: string) => licences.kinds?.find((k) => k.id === id)?.name ?? 'a licence';
  // Blueprint: a department's row has one action, its customer text; its name opens it
  const bp = useDesign() === 'blueprint';
  const fiveTypes = useFive(types.rows);
  return (
    <>
      {show !== 'types' && (
        <section className="sheet setup-card" aria-labelledby="bu-title">
          <div className="side-head">
            <h2 id="bu-title">Departments</h2>
            <div className="row">
              {/* Blueprint: the company's own is Company details › On your paperwork */}
              {show === 'all' && (
                <button className="btn quiet" onClick={() => setText('company')}>
                  The company’s customer text
                </button>
              )}
              {canEdit && !mirroring && (
                <button className="btn" onClick={() => setUnit('new')}>
                  Add a department
                </button>
              )}
            </div>
          </div>
          {!canEdit && <ReadOnly who="Only the owner or a manager" />}
          {!units.rows ? (
            <Loading error={units.error} what="the departments" />
          ) : !units.rows.length ? (
            <p className="muted">No departments yet. Add your first, for example Plumbing.</p>
          ) : (
            <ul className="setup-list">
              {units.rows.map((u) => (
                <li key={u.id}>
                  <div>
                    {bp ? (
                      <button
                        type="button"
                        className="bp-name"
                        aria-label={`${canEdit ? 'Edit' : 'Open'} ${u.name}`}
                        onClick={() => setUnit(u)}
                      >
                        <b>{u.name}</b>
                      </button>
                    ) : (
                      <b>{u.name}</b>
                    )}
                    <span className="muted small">
                      {' '}
                      · {u.defaultInvoiceMode === 'flat_rate' ? 'Flat rate' : 'Time and materials'}
                    </span>
                    {!u.active && <span className="chip say"> Archived</span>}
                  </div>
                  <div className="row">
                    <button
                      className="btn quiet sm"
                      aria-label={`Customer text for ${u.name}`}
                      onClick={() => setText(u)}
                    >
                      Customer text
                    </button>
                    {!bp && (
                      <button
                        className="btn quiet sm"
                        aria-label={`${canEdit ? 'Edit' : 'Open'} ${u.name}`}
                        onClick={() => setUnit(u)}
                      >
                        {canEdit ? 'Edit' : 'Open'}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {show !== 'departments' && (
        <>
          <section className="sheet setup-card" aria-labelledby="jt-title">
            <div className="side-head">
              <h2 id="jt-title">Your job types</h2>
              {canEdit && !mirroring && (
                <button className="btn primary" onClick={() => setType('new')}>
                  Add a job type
                </button>
              )}
            </div>
            {!types.rows ? (
              <Loading error={types.error} what="the job types" />
            ) : !types.rows.length ? (
              <p className="muted">No job types yet.</p>
            ) : (
              <ul className="setup-list">
                {(fiveTypes.rows ?? types.rows).map((j) => (
                  <li key={j.id}>
                    <div>
                      <b>{j.name}</b>
                      <span className="muted small">
                        {' '}
                        · {unitName(j.businessUnitId)} · {j.defaultDurationMinutes} min
                        {j.skills.length ? ` · ${j.skills.join(', ')}` : ''}
                        {j.requiredLicenceKindIds.length
                          ? ` · needs ${j.requiredLicenceKindIds.map(kindName).join(', ')}`
                          : ''}
                      </span>
                      {!j.active && <span className="chip say"> Not offered</span>}
                    </div>
                    {canEdit && (
                      <button
                        className="btn quiet sm"
                        aria-label={`Edit ${j.name}`}
                        onClick={() => setType(j)}
                      >
                        Edit
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {fiveTypes.more}
          </section>
          {/* the licences the company tracks, which a job type can need (BL-027) */}
          <LicenceKindsCard canEdit={canEdit} list={licences} />
        </>
      )}
      {unit && (
        <UnitSheet
          unit={unit === 'new' ? null : unit}
          canEdit={canEdit}
          ownOnly={mirroring}
          onSaved={() => {
            setUnit(null);
            void units.reload();
          }}
          onClose={() => setUnit(null)}
        />
      )}
      {text && (
        <CustomerTextSheet
          unit={text === 'company' ? null : text}
          canEdit={canEdit}
          onClose={() => setText(null)}
        />
      )}
      {type && (
        <JobTypeSheet
          type={type === 'new' ? null : type}
          ownOnly={mirroring}
          units={units.rows ?? []}
          kinds={licences.kinds ?? []}
          onSaved={() => {
            setType(null);
            void types.reload();
          }}
          onClose={() => setType(null)}
        />
      )}
    </>
  );
}

/**
 * Company details › On your paperwork (Blueprint, the setup audit): what customers read from the
 * company, its name, phone, email, address and licence and the invoice's words, with one way to change
 * it, the customer-text sheet a department's own values sit beside.
 */
export function PaperworkSection({ canEdit, held }: { canEdit: boolean; held: readonly string[] }) {
  const [rows, setRows] = useState<SettingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      setRows((await client.call<{ settings: SettingRow[] }>('settings.list', {})).settings);
      setError(null);
    } catch (e) {
      setError(refusal(e));
    }
  }, []);
  useEffect(() => void load(), [load]);
  const form = rows ? textForm(rows, null) : null;
  const value = (key: string) => form?.[key]?.text.trim() ?? '';
  return (
    <section className="sheet setup-card" aria-labelledby="paper-title">
      <div className="side-head">
        <h2 id="paper-title">On your paperwork</h2>
        <button className="btn quiet" onClick={() => setOpen(true)}>
          {canEdit ? 'Change' : 'Open'}
        </button>
      </div>
      <p className="muted small">
        What customers read on estimates, invoices and messages, unless a department has its own (under
        Departments).
      </p>
      {!form ? (
        <Loading error={error} what="the customer text" />
      ) : (
        <dl className="kv">
          {CUSTOMER_TEXT_FIELDS.map((f) => (
            <Fragment key={f.key}>
              <dt>{f.label}</dt>
              <dd>
                {f.kind === 'paragraph'
                  ? value(f.key)
                    ? 'Set'
                    : 'Not set'
                  : value(f.key).replaceAll('\n', ', ') || 'Not set'}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
      {/* the terms version a signature records, beside the words signed */}
      <SectionRules id="company.paper" rights={settingsRights(held)} />
      {open && (
        <CustomerTextSheet
          unit={null}
          canEdit={canEdit}
          onClose={() => {
            setOpen(false);
            void load();
          }}
        />
      )}
    </section>
  );
}

/**
 * What customers read from the company or one department (BL-052): its name, phone, email, address
 * and licence, the invoice's header and footer, and the paragraphs a customer signs. A department's
 * field is "Same as the company" until it has its own. Each change is one settings.set or
 * settings.clear, so a department given back to the company follows the company's later edits.
 */
function CustomerTextSheet({
  unit,
  canEdit,
  onClose,
}: {
  unit: Unit | null;
  canEdit: boolean;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<SettingRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [was, setWas] = useState<TextForm | null>(null);
  const [f, setF] = useState<TextForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const unitId = unit?.id ?? null;
  const load = useCallback(async () => {
    try {
      const r = await client.call<{ settings: SettingRow[] }>('settings.list', {});
      const form = textForm(r.settings, unitId);
      setRows(r.settings);
      setWas(form);
      setF(form);
      setLoadError(null);
    } catch (e) {
      setLoadError(refusal(e));
    }
  }, [unitId]);
  useEffect(() => {
    void load();
  }, [load]);
  const who = unit ? unit.name : 'the company';
  const companyName = (f?.['company.officialName']?.text || unit?.name) ?? '';
  const save = async () => {
    if (!was || !f) return;
    const built = textWrites(was, f, unitId);
    if ('error' in built) return setError(built.error);
    if (!built.writes.length) return setError('Nothing has changed.');
    setBusy(true);
    setError(null);
    try {
      for (const w of built.writes) await client.call(w.action, w.input);
      await load();
      setSaid(`Saved: what customers read from ${who}.`);
    } catch (e) {
      // the ones before it are saved: read again, so the form shows what is
      setError(refusal(e));
      await load();
    } finally {
      setBusy(false);
    }
  };
  const set = (key: string, patch: Partial<TextForm[string]>) => {
    if (!f) return;
    setF({ ...f, [key]: { ...f[key]!, ...patch } });
    setError(null);
    setSaid(null);
  };
  return (
    <Sheet
      title={unit ? `Customer text: ${unit.name}` : 'The company’s customer text'}
      wide
      onClose={onClose}
    >
      <p className="muted small">
        {unit
          ? `What customers of ${unit.name} see on its estimates, invoices and messages. A field left “Same as the company” follows the company’s.`
          : 'What customers see on estimates, invoices and messages, unless a department has its own.'}{' '}
        A signed paragraph is kept with each signature exactly as the customer read it.
      </p>
      {!rows || !f ? (
        <Loading error={loadError} what="the customer text" retry={() => void load()} />
      ) : (
        <fieldset className="plain" disabled={!canEdit || busy}>
          {CUSTOMER_TEXT_FIELDS.map((t) => {
            const v = f[t.key]!;
            const editing = !unit || v.own;
            const id = `ct-${t.key}`;
            return (
              <div key={t.key} className="ct-field">
                <div className="row">
                  <label className="flabel" htmlFor={editing ? id : undefined}>
                    {t.label}
                  </label>
                  {unit && (
                    <label className="checkrow small">
                      <input
                        type="checkbox"
                        checked={v.own}
                        onChange={(e) =>
                          set(t.key, { own: e.target.checked, text: e.target.checked ? v.text : v.company })
                        }
                      />{' '}
                      Own for {unit.name}
                    </label>
                  )}
                </div>
                {!editing ? (
                  <p className="muted small ct-same">
                    Same as the company{v.company ? `: ${v.company.replace(/\n/g, ' · ')}` : ' (none)'}
                  </p>
                ) : t.kind === 'line' ? (
                  <input
                    id={id}
                    className="field"
                    value={v.text}
                    placeholder={t.placeholder}
                    onChange={(e) => set(t.key, { text: e.target.value })}
                  />
                ) : (
                  <textarea
                    id={id}
                    className="field area"
                    rows={t.kind === 'lines' ? 3 : 4}
                    value={v.text}
                    onChange={(e) => set(t.key, { text: e.target.value })}
                  />
                )}
                {t.help && <p className="muted small">{t.help}</p>}
                {t.paragraph && editing && (
                  <>
                    {paragraphProblem(v.text, t.paragraph) && (
                      <p className="error small">{paragraphProblem(v.text, t.paragraph)}</p>
                    )}
                    <p className="muted small">
                      Fields:{' '}
                      {PARAGRAPH_FIELDS[t.paragraph].map(([k, what], i) => (
                        <Fragment key={k}>
                          {i ? ', ' : ''}
                          <code>{`{${k}}`}</code> {what}
                        </Fragment>
                      ))}
                      .
                    </p>
                    <p className="ct-preview small" aria-label={`${t.label}, as a customer reads it`}>
                      {paragraphPreview(v.text, companyName)}
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </fieldset>
      )}
      {said && (
        <p className="notice ok small" role="status">
          {said}
        </p>
      )}
      {canEdit ? (
        <SheetButtons
          w={{ busy, error }}
          label="Save"
          busyLabel="Saving…"
          disabled={!f}
          onSave={() => void save()}
          onCancel={onClose}
        />
      ) : (
        <ReadOnlySheet onClose={onClose}>{null}</ReadOnlySheet>
      )}
    </Sheet>
  );
}

function UnitSheet({
  unit,
  canEdit,
  ownOnly = false,
  onSaved,
  onClose,
}: {
  unit: Unit | null;
  canEdit: boolean;
  /** While the company mirrors ServiceTitan: its defaults only, never its name or archiving (BL-097). */
  ownOnly?: boolean;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [f, setF] = useState<UnitForm>(() => (unit ? { ...unit } : emptyUnitForm()));
  // "The customer signs to accept the finished work" is the setting invoices.signatureRequired for
  // this department (BL-052): the company's until it's changed here
  const [sig, setSig] = useState<{ was: boolean; now: boolean } | null>(null);
  const unitId = unit?.id;
  useEffect(() => {
    void client
      .call<{ value: boolean }>('settings.get', {
        key: 'invoices.signatureRequired',
        scope: unitId ? { businessUnitId: unitId } : {},
      })
      .then((r) => setSig({ was: r.value, now: r.value }))
      .catch(() => setSig(null));
  }, [unitId]);
  const w = useWrite(JSON.stringify([f, sig?.now]));
  const save = async () => {
    const built = ownOnly && unit ? unitDefaultsInput(unit, f) : unitInput(unit, f);
    const sigChanged = !!sig && sig.now !== sig.was;
    let id = unit?.id;
    if (!(sigChanged && 'error' in built && built.error === NOTHING)) {
      const r = await w.run<Unit>(
        ownOnly ? 'businessUnits.setDefaults' : unit ? 'businessUnits.update' : 'businessUnits.create',
        built,
      );
      if (!r) return;
      id = r.id;
    }
    if (sigChanged && id) {
      const s = await w.run(
        'settings.set',
        {
          input: {
            key: 'invoices.signatureRequired',
            scopeType: 'business_unit',
            scopeId: id,
            value: sig.now,
          },
        },
        'signature',
      );
      if (!s) return;
    }
    onSaved();
  };
  return (
    <Sheet title={unit ? unit.name : 'Add a department'} onClose={onClose}>
      {!canEdit && unit ? (
        <ReadOnlySheet onClose={onClose}>
          <dl className="kv item-facts">
            <dt>How its jobs are priced</dt>
            <dd>
              {unit.defaultInvoiceMode === 'flat_rate'
                ? 'Flat rate (from the price book)'
                : 'Time and materials'}
            </dd>
            {UNIT_RULES.map(([k, label]) => (
              <Fragment key={k}>
                <dt>{label}</dt>
                <dd>{unit[k] ? 'Yes' : 'No'}</dd>
              </Fragment>
            ))}
            {!unit.active && (
              <>
                <dt>Archived</dt>
                <dd>No new jobs, history kept</dd>
              </>
            )}
          </dl>
        </ReadOnlySheet>
      ) : (
        <>
          <fieldset className="plain">
            {ownOnly && <p className="muted small">Its name and archiving: {MIRROR_LINE}</p>}
            <div className="fgrid">
              <label className="f">
                Name
                <input
                  className="field"
                  value={f.name}
                  readOnly={ownOnly}
                  onChange={(e) => setF({ ...f, name: e.target.value })}
                  autoFocus
                />
              </label>
              <label className="f">
                How its jobs are priced
                <select
                  className="field"
                  value={f.defaultInvoiceMode}
                  onChange={(e) =>
                    setF({ ...f, defaultInvoiceMode: e.target.value as UnitForm['defaultInvoiceMode'] })
                  }
                >
                  <option value="flat_rate">Flat rate (from the price book)</option>
                  <option value="time_and_materials">Time and materials</option>
                </select>
              </label>
            </div>
            {UNIT_RULES.map(([k, label]) => (
              <label key={k} className="checkrow">
                <input
                  type="checkbox"
                  checked={f[k]}
                  onChange={(e) => setF({ ...f, [k]: e.target.checked })}
                />{' '}
                {label}
              </label>
            ))}
            {sig && (
              <label className="checkrow">
                <input
                  type="checkbox"
                  checked={sig.now}
                  onChange={(e) => setSig({ ...sig, now: e.target.checked })}
                />{' '}
                The customer signs to accept the finished work
              </label>
            )}
            {unit && !ownOnly && (
              <label className="checkrow">
                <input
                  type="checkbox"
                  checked={!f.active}
                  onChange={(e) => setF({ ...f, active: !e.target.checked })}
                />{' '}
                Archived: no new jobs, history kept
              </label>
            )}
          </fieldset>
          {unit && (
            <p className="muted small">A change applies to new jobs; jobs already booked keep theirs.</p>
          )}
          <SheetButtons
            w={w}
            label={unit ? 'Save' : 'Add department'}
            busyLabel="Saving…"
            disabled={!f.name.trim()}
            onSave={() => void save()}
            onCancel={onClose}
          />
        </>
      )}
    </Sheet>
  );
}

function JobTypeSheet({
  type,
  ownOnly = false,
  units,
  kinds,
  onSaved,
  onClose,
}: {
  type: JobType | null;
  /** While the company mirrors ServiceTitan: its own columns only, never its name, department or offering (BL-098). */
  ownOnly?: boolean;
  units: Unit[];
  /** The licences the company tracks (BL-027), retired ones too, for a type that still names one. */
  kinds: LicenceKind[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(() => jobTypeForm(type));
  const w = useWrite(JSON.stringify(f));
  const set = (k: keyof typeof f) => (e: { target: { value: string; checked?: boolean; type?: string } }) => {
    setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    w.setError(null);
  };
  const save = async () => {
    const r =
      ownOnly && type
        ? await w.run<JobType>('jobTypes.setDefaults', jobTypeDefaultsInput(type, f))
        : await w.run<JobType>(type ? 'jobTypes.update' : 'jobTypes.create', jobTypeInput(type, f));
    if (r) onSaved();
  };
  return (
    <Sheet title={type ? type.name : 'Add a job type'} onClose={onClose}>
      {ownOnly && <p className="muted small">Its name, department and offering: {MIRROR_LINE}</p>}
      <div className="fgrid">
        <label className="f">
          Name
          <input className="field" value={f.name} readOnly={ownOnly} onChange={set('name')} autoFocus />
        </label>
        <label className="f">
          Department
          <select
            className="field"
            value={f.businessUnitId}
            disabled={ownOnly}
            onChange={set('businessUnitId')}
          >
            <option value="">Any department</option>
            {units
              .filter((u) => u.active || u.id === f.businessUnitId)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
          </select>
        </label>
        <label className="f">
          Usually
          <select className="field" value={f.defaultKind} onChange={set('defaultKind')}>
            <option value="">Either</option>
            <option value="service">Service (the work)</option>
            <option value="estimate_visit">An estimate visit</option>
          </select>
        </label>
        <label className="f">
          Usual length (minutes)
          <input className="field num" inputMode="numeric" value={f.duration} onChange={set('duration')} />
        </label>
      </div>
      <label className="f">
        Skills it needs
        <input className="field" value={f.skills} onChange={set('skills')} placeholder="gas, backflow" />
      </label>
      {kinds.some((k) => k.active || f.licences.includes(k.id)) && (
        <fieldset className="plain checks-list">
          <legend className="h4">Licences it needs</legend>
          <p className="muted small">
            Someone on each visit holds every one ticked, valid on the visit’s day; your settings say whether
            a visit without warns or is refused.
          </p>
          {kinds
            .filter((k) => k.active || f.licences.includes(k.id))
            .map((k) => (
              <label key={k.id} className="check-row">
                <input
                  type="checkbox"
                  checked={f.licences.includes(k.id)}
                  onChange={(e) => {
                    setF({
                      ...f,
                      licences: e.target.checked
                        ? [...f.licences, k.id]
                        : f.licences.filter((x) => x !== k.id),
                    });
                    w.setError(null);
                  }}
                />
                <span>
                  {k.name}
                  {!k.active && <span className="muted small"> (no longer tracked)</span>}
                </span>
              </label>
            ))}
        </fieldset>
      )}
      {type && !ownOnly && (
        <label className="checkrow">
          <input type="checkbox" checked={f.active} onChange={set('active')} /> Offered when booking
        </label>
      )}
      <SheetButtons
        w={w}
        label={type ? 'Save' : 'Add job type'}
        busyLabel="Saving…"
        disabled={!f.name.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ───────── tax zones ─────────

const CLASS_WORD: Record<string, string> = { standard: 'standard', labour: 'labour', materials: 'materials' };

export function TaxTab({ canEdit, today }: { canEdit: boolean; today: string }) {
  const bp = useDesign() === 'blueprint';
  const [zones, setZones] = useState<Zone[] | null>(null);
  const [def, setDef] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<{ k: 'zone' } | { k: 'rate'; zone: Zone } | null>(null);
  const load = useCallback(() => {
    client
      .call<{ zones: Zone[]; defaultZoneId: string | null }>('tax.listZones', {})
      .then((r) => {
        setZones(r.zones);
        setDef(r.defaultZoneId);
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
  }, []);
  useEffect(load, [load]);
  return (
    <section className="sheet setup-card" aria-labelledby="tax-title">
      <div className="side-head">
        <h2 id="tax-title">Tax zones</h2>
        {canEdit && (
          <button className="btn primary" onClick={() => setSheet({ k: 'zone' })}>
            Add a tax zone
          </button>
        )}
      </div>
      {!canEdit && <ReadOnly who="Only the owner or a manager" />}
      {!zones ? (
        <Loading error={error} what="the tax zones" />
      ) : !zones.length ? (
        <p className="muted">No tax zones yet. Add the province you work in, with its rates.</p>
      ) : (
        <ul className="setup-list zones">
          {zones.map((z) => {
            const { now, later } = ratesNow(z, today);
            return (
              <li key={z.id}>
                <div>
                  <b>{z.name}</b>
                  {z.id === def && <span className="chip say ok"> Invoices use this</span>}
                  {z.active === false && <span className="chip say off"> Retired in ServiceTitan</span>}
                  <ul className="rates">
                    {now.map((c) => (
                      <li key={c.id}>
                        {c.code} {percentOf(c.rate)}
                        <span className="muted small">
                          {' '}
                          on {c.appliesTo.map((a) => CLASS_WORD[a] ?? a).join(', ')}
                        </span>
                      </li>
                    ))}
                    {later.map((c) => (
                      <li key={c.id} className="later">
                        {c.code} changes to {percentOf(c.rate)} on {c.effectiveFrom}
                      </li>
                    ))}
                  </ul>
                </div>
                {canEdit && (
                  <button
                    className="btn quiet sm"
                    aria-label={`Add a rate to ${z.name}`}
                    onClick={() => setSheet({ k: 'rate', zone: z })}
                  >
                    Add a rate
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {/* Blueprint: which zone invoices use, chosen here (tax.defaultZoneId; the setup audit) */}
      {bp && zones && zones.length > 0 && (
        <DefaultZone zones={zones} current={def} canEdit={canEdit} onSaved={load} />
      )}
      <p className="muted small">
        Rates already used on invoices stay as they were: a change starts on a date.
      </p>
      {sheet?.k === 'zone' && (
        <ZoneSheet
          today={today}
          onSaved={() => {
            setSheet(null);
            load();
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.k === 'rate' && (
        <RateSheet
          zone={sheet.zone}
          today={today}
          onSaved={() => {
            setSheet(null);
            load();
          }}
          onClose={() => setSheet(null)}
        />
      )}
    </section>
  );
}

function RateFields({
  r,
  onChange,
  codes,
}: {
  r: RateForm;
  onChange: (r: RateForm) => void;
  codes?: string[];
}) {
  return (
    <div className="fgrid">
      <label className="f">
        Tax
        {codes ? (
          <select className="field" value={r.code} onChange={(e) => onChange({ ...r, code: e.target.value })}>
            {codes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="field"
            value={r.code}
            onChange={(e) => onChange({ ...r, code: e.target.value })}
            placeholder="GST"
          />
        )}
      </label>
      <label className="f">
        Rate (%)
        <input
          className="field num"
          inputMode="decimal"
          value={r.percent}
          onChange={(e) => onChange({ ...r, percent: e.target.value })}
          placeholder="5"
        />
      </label>
      <label className="f">
        From
        <input
          className="field"
          type="date"
          value={r.from}
          onChange={(e) => onChange({ ...r, from: e.target.value })}
        />
      </label>
      <fieldset className="plain f">
        <legend className="flabel">On</legend>
        {TAX_CLASSES.map(([k, label]) => (
          <label key={k} className="checkrow">
            <input
              type="checkbox"
              checked={r.appliesTo.includes(k)}
              onChange={(e) =>
                onChange({
                  ...r,
                  appliesTo: e.target.checked ? [...r.appliesTo, k] : r.appliesTo.filter((x) => x !== k),
                })
              }
            />{' '}
            {label}
          </label>
        ))}
      </fieldset>
    </div>
  );
}

function ZoneSheet({ today, onSaved, onClose }: { today: string; onSaved: () => void; onClose: () => void }) {
  const [name, setName] = useState('');
  const [rates, setRates] = useState<RateForm[]>(() => [emptyRateForm(today)]);
  const w = useWrite(JSON.stringify([name, rates]));
  const save = async () => {
    const r = await w.run<Zone>('tax.createZone', zoneInput(name, rates));
    if (r) onSaved();
  };
  return (
    <Sheet title="Add a tax zone" wide onClose={onClose}>
      <label className="f">
        Zone
        <input
          className="field"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="British Columbia"
          autoFocus
        />
      </label>
      {rates.map((r, i) => (
        <div key={i} className="rate-block">
          <RateFields r={r} onChange={(x) => setRates(rates.map((y, j) => (j === i ? x : y)))} />
          {rates.length > 1 && (
            <button className="btn quiet sm" onClick={() => setRates(rates.filter((_, j) => j !== i))}>
              Remove this tax
            </button>
          )}
        </div>
      ))}
      <div className="row">
        <button
          className="btn quiet"
          onClick={() => setRates([...rates, { ...emptyRateForm(today), appliesTo: ['materials'] }])}
        >
          Add another tax
        </button>
      </div>
      <SheetButtons
        w={w}
        label="Add zone"
        busyLabel="Adding…"
        disabled={!name.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

function RateSheet({
  zone,
  today,
  onSaved,
  onClose,
}: {
  zone: Zone;
  today: string;
  onSaved: () => void;
  onClose: () => void;
}) {
  const codes = [...new Set(zone.components.map((c) => c.code))];
  const [r, setR] = useState<RateForm>(() => {
    const cur = ratesNow(zone, today).now[0];
    return { ...emptyRateForm(today), code: codes[0] ?? '', appliesTo: cur?.appliesTo ?? ['standard'] };
  });
  const [isNew, setIsNew] = useState(!codes.length);
  const w = useWrite(JSON.stringify(r));
  const save = async () => {
    const c = componentInput(r);
    const res = await w.run<Zone>(
      'tax.addRate',
      'error' in c ? c : { input: { taxZoneId: zone.id, component: c.component } },
    );
    if (res) onSaved();
  };
  return (
    <Sheet title={`A new rate in ${zone.name}`} wide onClose={onClose}>
      {codes.length > 0 && (
        <label className="checkrow">
          <input
            type="checkbox"
            checked={isNew}
            onChange={(e) => {
              setIsNew(e.target.checked);
              setR({ ...r, code: e.target.checked ? '' : codes[0]! });
            }}
          />{' '}
          A tax this zone doesn’t have yet
        </label>
      )}
      <RateFields r={r} onChange={setR} {...(isNew ? {} : { codes })} />
      <p className="muted small">The rate in force now ends the day before this one starts.</p>
      <SheetButtons
        w={w}
        label="Add rate"
        busyLabel="Adding…"
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ───────── message templates ─────────

const PURPOSE_WORDS: Record<string, string> = {};
const purposeOf = (t: Template) => {
  const w = PURPOSE_WORDS[t.purpose] ?? t.purpose.replace(/_/g, ' ');
  return w.charAt(0).toUpperCase() + w.slice(1);
};

export function TemplatesTab({
  canEdit,
  title = 'Message templates',
  children,
}: {
  canEdit: boolean;
  /** Blueprint's name for the list (Texts and email › The words). */
  title?: string;
  /** More of the words, under the templates (Blueprint: the missed-call text's). */
  children?: ReactNode;
}) {
  const list = useList<Template>('templates.list', {}, (r: { templates: Template[] }) => r.templates);
  const [open, setOpen] = useState<Template | null>(null);
  const five = useFive(list.rows);
  return (
    <section className="sheet setup-card" aria-labelledby="tpl-title">
      <div className="side-head">
        <h2 id="tpl-title">{title}</h2>
      </div>
      <p className="muted small">
        The words customers get in texts and emails.{' '}
        {canEdit ? '' : 'Only the owner or a manager changes these.'}
      </p>
      {!list.rows ? (
        <Loading error={list.error} what="the templates" />
      ) : (
        <ul className="setup-list">
          {(five.rows ?? list.rows).map((t) => (
            <li key={t.id}>
              <div>
                <b className="cap">{purposeOf(t)}</b>
                <span className="chip say"> {t.channel === 'sms' ? 'Text' : 'Email'}</span>
                <p className="muted small tpl-body">
                  {t.subject ? `${t.subject}: ` : ''}
                  {t.body}
                </p>
              </div>
              <button
                className="btn quiet sm"
                aria-label={`${canEdit ? 'Edit' : 'Open'} ${purposeOf(t)}`}
                onClick={() => setOpen(t)}
              >
                {canEdit ? 'Edit' : 'Open'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {five.more}
      {children}
      {open && (
        <TemplateSheet
          t={open}
          canEdit={canEdit}
          onSaved={() => {
            setOpen(null);
            void list.reload();
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

function TemplateSheet({
  t,
  canEdit,
  onSaved,
  onClose,
}: {
  t: Template;
  canEdit: boolean;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [subject, setSubject] = useState(t.subject ?? '');
  const [body, setBody] = useState(t.body);
  const w = useWrite(`${subject}|${body}`);
  const unknown = unknownFields(t, `${subject} ${body}`);
  const insert = (f: string) => setBody(`${body}${body.endsWith(' ') || !body ? '' : ' '}{{${f}}}`);
  const save = async () => {
    const r = await w.run<Template>('templates.update', templateInput(t, subject, body));
    if (r) onSaved();
  };
  return (
    <Sheet title={`${purposeOf(t)} (${t.channel === 'sms' ? 'text' : 'email'})`} wide onClose={onClose}>
      {!canEdit ? (
        <ReadOnlySheet onClose={onClose}>
          <dl className="kv item-facts">
            {t.channel === 'email' && (
              <>
                <dt>Subject</dt>
                <dd>{t.subject || '—'}</dd>
              </>
            )}
            <dt>Message</dt>
            <dd className="item-desc">{t.body}</dd>
          </dl>
        </ReadOnlySheet>
      ) : (
        <>
          <fieldset className="plain">
            {t.channel === 'email' && (
              <label className="f">
                Subject
                <input className="field" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </label>
            )}
            <label className="f">
              Message
              <textarea
                className="field area tall"
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  w.setError(null);
                }}
                maxLength={4000}
              />
            </label>
            {t.fields.length > 0 && (
              <div className="flabel">
                Put in
                <div className="chips">
                  {t.fields.map((f) => (
                    <button key={f} type="button" className="btn quiet sm" onClick={() => insert(f)}>
                      {`{{${f}}}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </fieldset>
          {unknown.length > 0 && (
            <p className="notice warn">
              This message can’t fill in {unknown.map((f) => `{{${f}}}`).join(', ')}. Use one of the fields
              above.
            </p>
          )}
          {t.channel === 'sms' && (
            <p className="muted small">
              {body.length} characters. Texts over 160 go as more than one message.
            </p>
          )}
          <SheetButtons
            w={w}
            label="Save"
            busyLabel="Saving…"
            disabled={unknown.length > 0}
            onSave={() => void save()}
            onCancel={onClose}
          />
        </>
      )}
    </Sheet>
  );
}

// ───────── a truck or a warehouse (Inventory) ─────────

export function StockLocationSheet({ onSaved, onClose }: { onSaved: () => void; onClose: () => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'warehouse' | 'truck'>('truck');
  const [staffId, setStaffId] = useState('');
  const techs = useList<Staff>('staff.list', { techniciansOnly: true }, (r: { staff: Staff[] }) => r.staff);
  // a technician drives one truck: those who have one aren't offered again
  const trucks = useList<{ kind: string; staffId: string | null; active: boolean }>(
    'stockLocations.list',
    {},
    (r: { locations: { kind: string; staffId: string | null; active: boolean }[] }) => r.locations,
  );
  const driving = new Set(
    (trucks.rows ?? []).filter((l) => l.kind === 'truck' && l.active).map((l) => l.staffId),
  );
  const free = (techs.rows ?? []).filter((s) => !driving.has(s.id));
  const w = useWrite(JSON.stringify([name, kind, staffId]));
  const save = async () => {
    const r = await w.run<{ id: string }>('stockLocations.create', stockInput(name, kind, staffId));
    if (r) onSaved();
  };
  return (
    <Sheet title="Add a truck or warehouse" onClose={onClose}>
      <div className="fgrid">
        <label className="f">
          It’s a
          <select
            className="field"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'warehouse' | 'truck')}
          >
            <option value="truck">Truck</option>
            <option value="warehouse">Warehouse</option>
          </select>
        </label>
        <label className="f">
          Name
          <input
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={kind === 'truck' ? 'Truck 3' : 'Main warehouse'}
            autoFocus
          />
        </label>
        {kind === 'truck' && (
          <label className="f">
            Driven by
            <select className="field" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
              <option value="">No one yet</option>
              {free.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {kind === 'truck' && (
        <p className="muted small">
          The technician sees this truck’s stock and picklist on their iPad.
          {techs.rows && !free.length ? ' Every technician has a truck already.' : ''}
        </p>
      )}
      <SheetButtons
        w={w}
        label={kind === 'truck' ? 'Add truck' : 'Add warehouse'}
        busyLabel="Adding…"
        disabled={!name.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

/** People's rows carry no profile; this reads the person's (staff.list) and opens their sheet. */
export function EditProfile({
  staffId,
  timeZone,
  onSaved,
  onClose,
}: {
  staffId: string;
  timeZone: string;
  onSaved: () => void;
  onClose: () => void;
}) {
  const all = useList<Staff>('staff.list', { includeInactive: true }, (r: { staff: Staff[] }) => r.staff);
  const person = all.rows?.find((s) => s.id === staffId);
  if (!person)
    return (
      <Sheet title="Profile" onClose={onClose}>
        <Loading error={all.rows ? 'That person isn’t on the staff list.' : all.error} what="their profile" />
      </Sheet>
    );
  return (
    <ProfileSheet
      person={person}
      timeZone={timeZone}
      onSaved={onSaved}
      onPhoto={() => void all.reload()}
      onClose={onClose}
    />
  );
}

/** Sales tax › Tax zones (Blueprint): the zone an address with none of its own is charged. */
function DefaultZone({
  zones,
  current,
  canEdit,
  onSaved,
}: {
  zones: readonly { id: string; name: string }[];
  current: string | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [pick, setPick] = useState(current ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setPick(current ?? ''), [current]);
  const name = zones.find((z) => z.id === current)?.name;
  if (!canEdit)
    return (
      <p className="small">
        An address with no zone of its own is charged {name ?? 'no sales tax until one is chosen'}.
      </p>
    );
  return (
    <div className="row wrap">
      <label className="small" htmlFor="tax-default">
        An address with no zone of its own is charged
      </label>
      <select id="tax-default" className="field" value={pick} onChange={(e) => setPick(e.target.value)}>
        {!current && <option value="">Choose a zone</option>}
        {zones.map((z) => (
          <option key={z.id} value={z.id}>
            {z.name}
          </option>
        ))}
      </select>
      {/* soft: Add a tax zone is the section's one primary */}
      {pick && pick !== current && (
        <button
          className="btn soft"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            client
              .call('settings.set', { key: 'tax.defaultZoneId', value: pick })
              .then(() => {
                setError(null);
                onSaved();
              })
              .catch((e: unknown) => setError(refusal(e)))
              .finally(() => setBusy(false));
          }}
        >
          Save
        </button>
      )}
      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
